// Listing modes for Tool Requests & Usage — Calendar / All / Authorized / Dispatched / Returned.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/bookings/modes.ts and src/flight_schedule/modes.ts, themselves
// mirrored from src/blank/BlankLayout.tsx (Rule 0 — the canonical reference).
//
//   <base>/calendar    → ToolRequestCalendar (visual scheduler / reservation board)
//   <base>/all         → ToolRequestList     (all requisitions & usage records)
//   <base>/authorised  → ToolRequestList     (status 3: authorized / ready for dispatch)
//   <base>/dispatched  → ToolRequestList     (status 4: dispatched / in use with engineers)
//   <base>/returned    → ToolRequestList     (status 5: returned to stores / closed)
//   <base>/create, <base>/:id → SIBLINGS of this layout (index.tsx)

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label.
 * Keys ARE the child route paths in index.tsx.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  calendar: "Calendar",
  all: "All Requests",
  authorised: "Authorized",
  dispatched: "Dispatched",
  returned: "Returned",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  calendar: "Request dates by status",
  all: "Every tool request",
  authorised: "Ready for dispatch",
  dispatched: "Out with engineers",
  returned: "Back in stores",
};

/**
 * Maps status-filtered mode segments to their corresponding backend status code.
 * Status codes from `service_requests_status` config (1:WIP, 2:Reserved, 3:Authorized, 4:Dispatched, 5:Returned).
 */
export const MODE_STATUS: { [slug: string]: number | undefined } = {
  all: undefined,
  authorised: 3,
  dispatched: 4,
  returned: 5,
};

export const DEFAULT_MODE_SEGMENT = "all";

/** `<module>.viewMode` — matching every other converted module's key shape. */
export const MODE_STORAGE_KEY = "tool_requests.viewMode";

/**
 * Is this a real mode segment?
 * Uses hasOwnProperty, NOT `in` — prototype chain safety.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    return DEFAULT_MODE_SEGMENT;
  }
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment.
 */
/** Module root (`/toolrequests` or `/app/toolrequests`), ignoring a mode or record segment. */
export function moduleBasePath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const idx = segments.lastIndexOf("toolrequests");
  if (idx === -1) return segments.length ? "/" + segments.join("/") : "/";
  return "/" + segments.slice(0, idx + 1).join("/");
}

export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
